import { useState, useEffect, useCallback, useContext } from 'react';
import { UserContext } from '../UserContext';
import { Package, Clock, Gift, ArrowUpCircle, AlertCircle, CheckCircle } from 'react-feather';

// Component Imports
import PageContainer from '../components/PageContainer';
import Modal from '../components/Modal';
import CreditBalance from './billing/CreditBalance';
import PaymentMethods from './billing/PaymentMethods';
import TransactionHistory from './billing/TransactionHistory';
import PurchaseCredits from './billing/PurchaseCredits';
import CancelAccount from './billing/CancelAccount';
import UpgradeSubscription from './billing/UpgradeSubscription';
import AutoReloadModal from './billing/AutoReloadModal';
import { OrgCapabilitiesView } from './Capabilities';
// Utility and Styling
import {
  fetchCreditBalance,
  fetchSubscriptionDetails,
  fetchUserProfile,
  fetchAutoReloadSettings
} from './billing/BillingUtils';
import './billing/billing.css';

const PLAN_PRICE_DETAILS = {
  lite: {
    monthly: parseFloat(process.env.REACT_APP_LITE_MONTHLY_PRICE || 97),
    yearly: parseFloat(process.env.REACT_APP_LITE_ANNUAL_PRICE || 970),
  },
  business: {
    monthly: parseFloat(process.env.REACT_APP_BUSINESS_PRICE || 347),
  },
  basic: {
    monthly: parseFloat(process.env.REACT_APP_BASIC_MONTHLY_PRICE || 197),
    yearly: parseFloat(process.env.REACT_APP_BASIC_ANNUAL_PRICE || 1997),
  },
  pro: {
    monthly: parseFloat(process.env.REACT_APP_PRO_MONTHLY_PRICE || 297),
    yearly: parseFloat(process.env.REACT_APP_PRO_ANNUAL_PRICE || 2997),
  },
};

const PLAN_ALIASES = {
  core: 'business',
  foundation: 'business',
};

const normalizePlanType = (planType) => {
  const normalized = planType?.toLowerCase?.().trim();
  return PLAN_ALIASES[normalized] || normalized;
};

const normalizeBillingCycle = (billingCycle) => {
  const normalized = billingCycle?.toLowerCase?.().trim();
  if (['annual', 'annually', 'year', 'yearly'].includes(normalized)) return 'yearly';
  return 'monthly';
};

const formatPlanPrice = (planType, billingCycle) => {
  const planKey = normalizePlanType(planType);
  const planPrices = PLAN_PRICE_DETAILS[planKey];
  if (!planPrices) return null;

  const cycle = normalizeBillingCycle(billingCycle);
  const availableCycle = planPrices[cycle] !== undefined ? cycle : Object.keys(planPrices)[0];
  const price = planPrices[availableCycle];

  if (!Number.isFinite(price)) return null;

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(price);

  return `${formattedPrice}/${availableCycle === 'yearly' ? 'year' : 'month'}`;
};

const Billing = () => {
  const { loadUserAddOns } = useContext(UserContext);
  
  // State
  const [creditBalance, setCreditBalance] = useState(0);
  // The monthly plan fee is intentionally not held here: the Current Plan
  // section below derives the price from the profile's plan type and billing
  // cycle, and the Credit Balance card no longer restates it.
  const [pageLoading, setPageLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  
  // NEW: Store specific settings here
  const [autoReloadSettings, setAutoReloadSettings] = useState(null); 

  const [isAutoReloadModalOpen, setAutoReloadModalOpen] = useState(false);
  // Modals
  const [isPurchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [isUpgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const handlePageError = useCallback((message) => {
    setPageMessage(message ? { type: 'error', message } : null);
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setPageLoading(true);
      setPageMessage(null);
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('access_token');

      // Fetch all data concurrently
      await loadUserAddOns();
      const [balanceRes, subscriptionRes, profileRes, autoReloadRes] = await Promise.all([
        fetchCreditBalance(userId, token),
        fetchSubscriptionDetails(userId, token),
        fetchUserProfile(token),
        fetchAutoReloadSettings(token),
      ]);

      // Update State
      if (balanceRes?.credit_balance !== undefined) {
        setCreditBalance(balanceRes.credit_balance);
      }
      if (subscriptionRes?.subscription) {
        setSubscription(subscriptionRes.subscription);
      } else {
        setSubscription(null);
      }
      setUserProfile(profileRes || null);
      setAutoReloadSettings(autoReloadRes?.settings || null);

    } catch (err) {
      setPageMessage({
        type: 'error',
        message: 'Failed to load billing information. Please refresh the page.',
      });
      console.error('Error loading billing information:', err);
    } finally {
      setPageLoading(false);
    }
  }, [loadUserAddOns]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Handlers
  const handlePurchaseSuccess = (newBalance) => {
    setCreditBalance(newBalance);
    setPurchaseModalOpen(false);
    // The purchase landed in the purchased-credit bucket, so refetch rather
    // than only updating the total, otherwise the breakdown rows would no
    // longer sum to the headline number.
    loadInitialData();
  };

  const handleUpgradeSuccess = (data) => {
      loadInitialData();
      setUpgradeModalOpen(false);
  };

  const getStatusDisplay = () => {
      if (subscription?.status) {
          return subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1);
      }
      if (userProfile?.plan_type) {
          return "Active"; 
      }
      return "Trial";
  };

  const planPrice = formatPlanPrice(userProfile?.plan_type, userProfile?.billing_cycle);

  return (
    <PageContainer
      loading={pageLoading}
      loadingMessage="Loading billing information..."
      title="Billing & Subscription"
      subtitle="Manage your subscription, credits, and payment details"
    >
        <div className="billing-page">

      {pageMessage && (
        <div className={`billing-page-alert ${pageMessage.type}`} role="alert">
          {pageMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{pageMessage.message}</span>
        </div>
      )}

      <div className="billing-layout">
        <CreditBalance
          creditBalance={creditBalance}
          onPurchaseClick={() => setPurchaseModalOpen(true)}
          onAutoReloadClick={() => setAutoReloadModalOpen(true)}
          autoReloadEnabled={autoReloadSettings?.enabled}
          autoReloadThreshold={autoReloadSettings?.threshold}
          autoReloadAmount={autoReloadSettings?.amount}
        />
        <PaymentMethods onStatusChange={setPageMessage} />
      </div>

      <h2 className="section-title">
        <Package className="section-icon" />
        Current Plan
      </h2>
      <div className="subscription-card">
        <div className="subscription-status-header">
            <div className="subscription-status">
                <h3>Subscription Status</h3>
                
                <div className="plan-details-container">
                    <span className="plan-name">
                        <strong>{userProfile?.plan_type ? userProfile.plan_type.toUpperCase() : 'UNKNOWN'}</strong>
                    </span>
                    {planPrice && (
                      <span className="subscription-plan-price">
                        {planPrice}
                      </span>
                    )}
                    <span className="status-badge">
                        {getStatusDisplay()}
                    </span>
                </div>
                
                {subscription?.trial_end && (
                    <p className="trial-date">Trial ends: {new Date(subscription.trial_end * 1000).toLocaleDateString()}</p>
                )}
            </div>
            
            <button 
                className="btn-change-plan"
                onClick={() => setUpgradeModalOpen(true)}
            >
                <ArrowUpCircle size={18} />
                Change Plan
            </button>
        </div>
      </div>

      {/* Capabilities Section */}
      <h2 className="section-title">
        <Gift className="section-icon" />
        Capabilities
      </h2>
      <div className="transaction-history-card">
        <div className="transaction-header">
          <div className="transaction-title">
            <div style={{ minWidth: 0 }}>
              <h3>Available Capabilities</h3>
              <p className="transaction-subtitle">
                Add-on capabilities available to your organization.
              </p>
            </div>
          </div>
        </div>
        <div className="transaction-history-content">
          <OrgCapabilitiesView searchable gridLayout />
        </div>
      </div>
      
      {/* Transaction History */}
      <div className="billing-layout">
        <div>
          <h2 className="section-title">
            <Clock className="section-icon" />
            Transaction History
          </h2>
          <TransactionHistory />
        </div>
      </div>
      
      <CancelAccount onError={handlePageError} />

      {/* Modals */}
      <Modal
        isOpen={isPurchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        title="Purchase Credits"
      >
        <PurchaseCredits onPurchaseSuccess={handlePurchaseSuccess} />
      </Modal>

    <Modal
        isOpen={isAutoReloadModalOpen}
        onClose={() => setAutoReloadModalOpen(false)}
        title="Configure Auto-Reload"
      >
         <AutoReloadModal 
            /* Pass the specific settings we fetched */
            currentSettings={autoReloadSettings} 
            onClose={() => setAutoReloadModalOpen(false)}
            onSave={() => {
                setAutoReloadModalOpen(false);
                loadInitialData(); 
            }}
         />
      </Modal>

      <Modal
        isOpen={isUpgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title="Change Subscription Plan"
      >
          <UpgradeSubscription 
            currentPlanType={userProfile?.plan_type} 
            onClose={() => setUpgradeModalOpen(false)}
            onUpdateSuccess={handleUpgradeSuccess}
          />
      </Modal>
            </div>
    </PageContainer>
  );
};

export default Billing;
